import { DraftPurchaseOrder } from './purchaseOrder'

export type Status = 'specified' | 'ordered' | 'delivered' | 'clientApproved'

export interface LineItemMaterial {
  id: string
  materialId: string
  status: Status
  quantity: number
  materialType?: 'finish' | 'accessory'
  hideOnDocument?: boolean
}

export interface Supplier {
  _id: string
  name: string
  contact?: {
    name?: string
    phone?: string
    email?: string
  }
  address?: string
  logo?: string
  notes?: string
  website?: string
  isArchived?: boolean
}

export interface Attachment {
  id: string
  name: string
  url: string
  fileType: string
  size: number
}

interface Image {
  url: string
  alt: string
}

export interface ProductLibraryItem {
  _id: string
  name: string
  supplier?: string
  supplierName?: string
  supplierItemCode?: string
  type: string
  timesUsed: number
  lineItem?: Pick<
    LineItem,
    | 'image'
    | 'description'
    | 'internalNotes'
    | 'brand'
    | 'productUrl'
    | 'dimensions'
    | 'additionalImages'
  >
  material?: Pick<Material, 'image' | 'materialType' | 'unit' | 'type' | 'id'>
  createdAt: string
}

export interface LineItem {
  id: string
  systemCategory?: string
  code: string
  name: string
  status: Status
  description: string
  quantity: number
  costPrice: number
  margin: number
  discount?: number
  taxRate?: number
  sellPrice: number
  supplier?: string
  supplierItemCode?: string
  purchaseOrderId?: string
  brand?: string
  productUrl?: string
  internalNotes?: string
  leadTime?: string
  materials?: LineItemMaterial[]
  dimensions?: {
    width?: number
    height?: number
    depth?: number
    unit?: string
  }
  groupId?: string
  image?: Image
  attachments?: Array<Attachment>
  additionalImages?: Array<Image>
}

export interface Material {
  id: string
  name: string
  type: string
  materialType: 'finish' | 'accessory'
  supplier?: string
  supplierItemCode?: string
  leadTime?: string
  purchaseOrderId?: string
  brand?: string
  productUrl?: string
  internalNotes?: string
  costPerUnit: number
  sellPricePerUnit: number
  unit?: string
  margin: number
  image?: Image
  attachments?: Array<Attachment>
}

export interface Group {
  id: string
  name: string
  groupId?: string
  isCollapsed?: boolean
  isAlternatives?: boolean
  selectedItemId?: string
}

export interface FloorPlan {
  id: string
  name: string
  type: 'plan' | 'render'
  imageUrl: string
  shapes: Shape[]
}

export interface Shape {
  id: string
  type: 'rectangle' | 'circle'
  x: number
  y: number
  width: number
  height: number
  linkedItemId?: string
  linkedGroupId?: string
}

export interface SupplierCosts {
  id: string
  supplierId: string
  deliveryCost?: number
  margin?: number
  notes?: string
}

export interface PurchaseOrderSettings {
  deliveryAddress: string
  deliveryNotes: string
  contact: {
    name: string
    phone: string
    email: string
  }
}

export type TeamMember = Pick<
  User,
  | '_id'
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'roles'
  | 'lastActive'
  | 'isInvited'
  | 'isDisabled'
>

export type Permission =
  | 'organisationUser'
  | 'userManagement'
  | 'organisationManagement'
export type Role = 'orgManager' | 'basic'

export enum RoleEnum {
  orgManager = 'Administrator',
  basic = 'Designer'
}

export interface User {
  createdAt: string
  email: string
  firstName: string
  isVerified: boolean
  isDemo?: boolean
  lastName: string
  organisationId: string
  permissions: Record<Permission, boolean>
  isGlobalAdmin?: boolean
  roles: Array<Role>
  lastActive: string
  isDisabled: boolean
  isInvited: boolean
  verifyExpires: null | string
  _id: string
}

export interface Company {
  _id?: string
  name: string
  address: string
  phone: string
  email: string
  logo: string
  country: string
  isXeroConnected?: boolean
  xeroNeedsReconnect?: boolean
  plan?: 'free' | 'standard' | 'premium'
  purchaseOrderSettings?: {
    internalDescriptionVisible?: boolean
    defaultDeliveryNotes?: string
  }
  projectSettings?: {
    defaultHeader?: string
    defaultFooter?: string
  }
  settings: {
    taxNumber: string
    primaryColor: string
    defaultMargin: number
    taxRate: number
  }
  createdAt?: string
}

export interface FFEDocument {
  _id?: string
  id: string
  title: string
  projectTitle: string
  projectDescription: string
  footerContent: string
  headerContent: string

  lineItems: LineItem[]
  groups: Group[]
  floorPlans: FloorPlan[]
  supplierCosts: SupplierCosts[]
  purchaseOrders: DraftPurchaseOrder[]
  purchaseOrderSettings: PurchaseOrderSettings
  materials: Material[]

  settings: {
    defaultMargin: number
    taxInclusive: boolean
    enableSpaceLinks: boolean
    showSupplierDetails: boolean
    showClientPrices: boolean
    showTotalValue: boolean
    showLeadTimes: boolean
    hideMaterialNames?: boolean
  }
  updatedAt?: string
  createdAt?: string
}
